import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitRideRating } from "../utils/api";
import "../css/riderating.css";

export default function RideRating({ ride, userId, driverName, driverId, onClose }) {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("travelo_token");

  // Verify passenger
  const isConfirmedPassenger = (ride?.confirmedPassengers || []).some(
    (p) => String(p._id || p) === String(userId)
  );

  if (!isConfirmedPassenger) return null;

  const getRatingText = (star) => {
    const texts = {
      1: { emoji: "😞", text: "Poor Experience" },
      2: { emoji: "😕", text: "Below Average" },
      3: { emoji: "😐", text: "Average" },
      4: { emoji: "😊", text: "Good Experience" },
      5: { emoji: "😍", text: "Excellent Experience" },
    };
    return texts[star] || {};
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError("⚠️ Please select a star rating first.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // ✅ THE FIX: Bulletproof ID extraction to prevent 400 Errors
      const targetDriverId = driverId || ride?.postedBy?._id || ride?.postedBy;
      
      const payload = {
        rating: Number(rating), // Ensure it's a strict number
        review: review.trim(),
        driverId: targetDriverId,
      };

      const data = await submitRideRating(ride._id, payload, token);

      if (data.success) {
        setSubmitted(true);
        setTimeout(() => {
          if (onClose) onClose();
          navigate("/profile");
        }, 2500);
      }
    } catch (err) {
      // Cleanly extract the exact error your backend sends back
      const errorMsg = err.response?.data?.error || err.message || "Failed to submit rating";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (window.confirm("Skip rating? You can always rate later from your profile.")) {
      if (onClose) onClose();
    }
  };

  // --- 1. Success Screen ---
  if (submitted) {
    return (
      <div className="rr-overlay">
        <div className="rr-card success-card">
          <div className="rr-success-icon">🎉</div>
          <h2 className="rr-title">Thank You!</h2>
          <p className="rr-subtitle">Your rating has been submitted.</p>
          
          <div className="rr-summary-box">
            <div className="rr-summary-item">
              <span>Rating:</span>
              <strong>{rating} / 5 ⭐</strong>
            </div>
            <div className="rr-summary-item">
              <span>Driver:</span>
              <strong>{driverName || "Your Driver"}</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- 2. Rating Screen ---
  return (
    <div className="rr-overlay">
      <div className="rr-card">
        <button className="rr-close-btn" onClick={handleSkip} disabled={loading} title="Close">✕</button>

        <div className="rr-header">
          <h2 className="rr-title">Rate Your Ride</h2>
          <p className="rr-subtitle">How was your trip with <strong>{driverName || "your driver"}</strong>?</p>
        </div>

        {/* Huge Interactive Stars */}
        <div className="rr-stars-container">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`rr-star-btn ${star <= (hoverRating || rating) ? "active" : ""}`}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
            >
              ★
            </button>
          ))}
        </div>

        {/* Dynamic Emoji Feedback */}
        <div className="rr-feedback-text">
          {(hoverRating || rating) > 0 ? (
            <>
              <span className="emoji">{getRatingText(hoverRating || rating).emoji}</span>
              <span className="text active-text">{getRatingText(hoverRating || rating).text}</span>
            </>
          ) : (
            <span className="text placeholder">Select a star to rate</span>
          )}
        </div>

        {/* Optional Review Textarea */}
        <div className="rr-review-section">
          <label>Share your feedback (Optional)</label>
          <textarea
            className="rr-textarea"
            placeholder="What did you like? What could be improved?"
            value={review}
            onChange={(e) => setReview(e.target.value.slice(0, 500))}
            rows="3"
          />
          <div className="rr-char-count">{review.length} / 500 characters</div>
        </div>

        {/* Error Banner */}
        {error && <div className="rr-error-banner">{error}</div>}

        {/* Action Buttons */}
        <div className="rr-actions">
          <button className="rr-btn skip" onClick={handleSkip} disabled={loading}>
            Skip for Now
          </button>
          <button className="rr-btn submit" onClick={handleSubmit} disabled={loading || rating === 0}>
            {loading ? "Submitting..." : "⭐ Submit Rating"}
          </button>
        </div>
      </div>
    </div>
  );
}